import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MParticleIntegration as UIAPIMParticleIntegration } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { clearMParticleIntegrationCache, setMParticleIntegrationCache } from './cache';
import {
  parseMParticleIntegration,
  type MParticleConnectInput,
  type MParticleIntegration,
  type MParticleSettingsInput,
} from './entity';

/**
 * mParticle connector writes: connect (POST), save settings (PUT), disconnect
 * (DELETE).
 *
 * The backend owns validation — it verifies the credentials against mParticle's
 * Events API before it writes anything — so saves are not optimistic. Each hook
 * writes the canonical response into the single detail slot the directory and
 * connections overview also read.
 *
 * Connect and save are not two spellings of one write: connect carries the
 * credentials and nothing else, while save carries all three settings and no
 * credentials. The credentials are create-only.
 */

function requireMParticle(res: { mparticle?: UIAPIMParticleIntegration }): UIAPIMParticleIntegration {
  if (res.mparticle == null) {
    throw new Error('mParticle response did not include an mparticle record');
  }
  return res.mparticle;
}

/**
 * The update reply wraps the record in `webhook`, not `mparticle`.
 *
 * That is the published contract, not a typo on this side: the Journeys
 * integrations skill in `customerio/services` documents it as a known quirk and
 * the Copilot eval spec pins it, so correcting it would break other readers.
 * Create and read both use `mparticle`; only this one reply differs.
 */
function requireUpdatedMParticle(res: { webhook?: UIAPIMParticleIntegration }): UIAPIMParticleIntegration {
  if (res.webhook == null) {
    throw new Error('mParticle update response did not include a record');
  }
  return res.webhook;
}

/* ------------------------------------------------------------------------- */
/* connectMParticleIntegration                                               */
/* ------------------------------------------------------------------------- */

export interface ConnectMParticleIntegrationInput extends MParticleConnectInput {
  workspaceId: number;
}

/**
 * @public Connect the workspace to mParticle.
 *
 * Only the credentials are sent, matching Ember's `createRecord({ key, secret })`.
 * The settings land at their zero values — no events, disabled, send-once — and
 * the config form is what fills them in afterwards.
 */
export async function connectMParticleIntegration({
  workspaceId,
  ...credentials
}: ConnectMParticleIntegrationInput): Promise<MParticleIntegration> {
  const res = await hydraUiApi.integrations.createMparticle({
    params: { path: { environment_id: String(workspaceId) } },
    body: { mparticle: credentials },
  });
  return parseMParticleIntegration(requireMParticle(res));
}

type UseConnectMParticleVariables = Omit<ConnectMParticleIntegrationInput, 'workspaceId'>;

interface UseConnectMParticleOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<MParticleIntegration, UseConnectMParticleVariables>;
}

/** @public */
export const useConnectMParticleIntegration = ({ workspaceId, mutationConfig }: UseConnectMParticleOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseConnectMParticleVariables) => connectMParticleIntegration({ workspaceId, ...variables }),
    onSuccess: (connected, ...callbackArgs) => {
      setMParticleIntegrationCache(qc, workspaceId, connected);
      onSuccess?.(connected, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* saveMParticleIntegration                                                  */
/* ------------------------------------------------------------------------- */

export interface SaveMParticleIntegrationInput extends MParticleSettingsInput {
  workspaceId: number;
  /** The integrations-table row id, from the loaded record. */
  integrationId: number;
}

/**
 * @public Save the connector's settings.
 *
 * The three settings go over the wire as given. The backend has its own rule —
 * it stores `enabled: false` when `events` is empty — but deciding that here
 * would silently disable a record the user never asked to disable. The save
 * button's own guard is what keeps an empty selection from being saved.
 */
export async function saveMParticleIntegration({
  workspaceId,
  integrationId,
  ...settings
}: SaveMParticleIntegrationInput): Promise<MParticleIntegration> {
  const res = await hydraUiApi.integrations.updateMparticle({
    params: { path: { environment_id: String(workspaceId), id: String(integrationId) } },
    body: { mparticle: settings },
  });
  return parseMParticleIntegration(requireUpdatedMParticle(res));
}

type UseSaveMParticleVariables = Omit<SaveMParticleIntegrationInput, 'workspaceId'>;

interface UseSaveMParticleOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<MParticleIntegration, UseSaveMParticleVariables>;
}

/** @public */
export const useSaveMParticleIntegration = ({ workspaceId, mutationConfig }: UseSaveMParticleOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseSaveMParticleVariables) => saveMParticleIntegration({ workspaceId, ...variables }),
    onSuccess: (saved, ...callbackArgs) => {
      setMParticleIntegrationCache(qc, workspaceId, saved);
      onSuccess?.(saved, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* disconnectMParticleIntegration                                            */
/* ------------------------------------------------------------------------- */

export interface DisconnectMParticleIntegrationInput {
  workspaceId: number;
  integrationId: number;
}

/** @public Remove the connector. Replies 204, so there is nothing to parse. */
export async function disconnectMParticleIntegration({
  workspaceId,
  integrationId,
}: DisconnectMParticleIntegrationInput): Promise<void> {
  await hydraUiApi.integrations.deleteMparticle({
    params: { path: { environment_id: String(workspaceId), id: String(integrationId) } },
  });
}

type UseDisconnectMParticleVariables = Omit<DisconnectMParticleIntegrationInput, 'workspaceId'>;

interface UseDisconnectMParticleOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, UseDisconnectMParticleVariables>;
}

/** @public */
export const useDisconnectMParticleIntegration = ({ workspaceId, mutationConfig }: UseDisconnectMParticleOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDisconnectMParticleVariables) =>
      disconnectMParticleIntegration({ workspaceId, ...variables }),
    onSuccess: (result, ...callbackArgs) => {
      clearMParticleIntegrationCache(qc, workspaceId);
      onSuccess?.(result, ...callbackArgs);
    },
  });
};
